import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CatalogCard } from '@/types/card'
import type { SearchHit } from './engine'
import { hatFilter, type SearchFilters } from './facetten'
import type { WorkerRequest, WorkerResponse } from './worker'

export type SearchStatus = 'lädt' | 'bereit' | 'fehler'

/**
 * Bindet den Such-Worker an React an.
 *
 * Antworten tragen ein Token: Bei schnellem Tippen treffen Ergebnisse gelegentlich in
 * anderer Reihenfolge ein als die Anfragen gestellt wurden. Ohne Token würde kurz das
 * Ergebnis eines älteren Suchbegriffs stehen bleiben.
 */
export function useSearch(
  cards: readonly CatalogCard[] | null,
  searchData: { index: string; fullTexts: Record<string, string> } | null,
) {
  const workerRef = useRef<Worker | null>(null)
  const tokenRef = useRef(0)
  const pendingRef = useRef(new Map<number, (hits: SearchHit[]) => void>())
  const [status, setStatus] = useState<SearchStatus>('lädt')

  useEffect(() => {
    if (!cards || !searchData) return

    const worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' })
    workerRef.current = worker
    const pending = pendingRef.current

    worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
      const message = event.data
      if (message.type === 'ready') setStatus('bereit')
      else if (message.type === 'error') setStatus('fehler')
      else if (message.type === 'results') {
        pending.get(message.token)?.(message.hits)
        pending.delete(message.token)
      }
    }
    worker.onerror = () => setStatus('fehler')

    const init: WorkerRequest = {
      type: 'init',
      index: searchData.index,
      fullTexts: searchData.fullTexts,
      cards: [...cards],
    }
    worker.postMessage(init)

    return () => {
      worker.terminate()
      workerRef.current = null
      pending.clear()
    }
  }, [cards, searchData])

  const search = useCallback((query: string, filters: SearchFilters = {}): Promise<SearchHit[]> => {
    const worker = workerRef.current
    if (!worker) return Promise.resolve([])

    const token = ++tokenRef.current
    return new Promise<SearchHit[]>((resolve) => {
      pendingRef.current.set(token, resolve)
      const request: WorkerRequest = { type: 'query', token, query, filters }
      worker.postMessage(request)
    })
  }, [])

  return useMemo(() => ({ search, status }), [search, status])
}

/**
 * Führt eine Suche aus und hält das Ergebnis. Verwirft Antworten, die zu einem
 * bereits überholten Suchbegriff gehören.
 */
export function useSearchResults(
  search: (query: string, filters: SearchFilters) => Promise<SearchHit[]>,
  query: string,
  filters: SearchFilters,
  /** Zustand des Suchworkers. Solange er lädt, gibt es noch kein Ergebnis — und erst
   *  recht kein leeres. */
  status: SearchStatus,
): { hits: SearchHit[]; searching: boolean } {
  // Das Ergebnis wird zusammen mit der Anfrage abgelegt, zu der es gehört. So lässt
  // sich beim Lesen entscheiden, ob es noch zur aktuellen Eingabe passt — ohne den
  // Zustand bei jeder Änderung im Effekt zurückzusetzen.
  const [result, setResult] = useState<{ key: string; hits: SearchHit[] }>({ key: '', hits: [] })

  const trimmed = query.trim()
  const filterKey = JSON.stringify(filters)
  const key = `${trimmed}\u0000${filterKey}`
  // Ein gesetzter Filter ist für sich schon eine vollständige Frage: „alle Karten zum
  // Gefahrgut" braucht kein Stichwort. Ohne beides gibt es dagegen nichts zu suchen.
  const zuSuchen = trimmed.length > 0 || hatFilter(filters)

  useEffect(() => {
    // Erst suchen, wenn der Index steht. Sonst käme eine leere Antwort zurück und
    // die Oberfläche meldete „nichts gefunden", obwohl noch gar nicht gesucht wurde —
    // genau das passiert, wenn jemand die App öffnet und sofort tippt.
    if (!zuSuchen || status !== 'bereit') return

    let cancelled = false
    void search(trimmed, filters).then((hits) => {
      if (!cancelled) setResult({ key, hits })
    })

    return () => {
      cancelled = true
    }
    // filters steckt in filterKey; das Objekt selbst ist bei jedem Rendern neu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, trimmed, filterKey, key, status, zuSuchen])

  if (!zuSuchen) return { hits: [], searching: false }
  if (status !== 'bereit') return { hits: [], searching: status === 'lädt' }
  // Solange das vorliegende Ergebnis zu einer älteren Eingabe gehört, läuft die Suche
  // noch — und es wird nicht kurz ein Treffer zum vorherigen Suchbegriff gezeigt.
  if (result.key !== key) return { hits: [], searching: true }
  return { hits: result.hits, searching: false }
}
